import { useLayoutEffect, useRef, type ReactNode } from 'react'
import { t } from '../copy'
import { IconButton } from './Button'
import { ChevronLeft } from './icons'

/**
 * One reading column (Astra §2): 20px margins on a phone; from 600px, 32px margins and up to 640px wide, centered.
 * `wide` is the desktop workspace: up to 1312px with 32px margins until 1440 and 64px above.
 */
export const COLUMN = 'mx-auto w-full max-w-md px-5 md:max-w-[704px] md:px-8'
export const WORKSPACE = 'mx-auto w-full max-w-md px-5 md:max-w-[704px] md:px-8 lg:max-w-[1376px] min-[1440px]:max-w-[1440px] min-[1440px]:px-16'

export function Page({ children, className = '', wide = false }: { children: ReactNode; className?: string; wide?: boolean }) {
  return <div className={`${wide ? WORKSPACE : COLUMN} ${className}`}>{children}</div>
}

/** Sticky translucent top bar with a back button. */
export function TopBar({ onBack, children, right, wide = false }: { onBack?: () => void; children?: ReactNode; right?: ReactNode; wide?: boolean }) {
  return (
    <header className="material sticky top-0 z-30 pt-[env(safe-area-inset-top)]">
      <div className={`mx-auto flex h-[52px] w-full max-w-md items-center gap-2 px-2 md:max-w-[704px] md:px-6 ${wide ? 'lg:max-w-[1104px]' : ''}`}>
        {onBack ? (
          <IconButton label={t('nav.back')} onClick={onBack}>
            <ChevronLeft />
          </IconButton>
        ) : (
          <span className="size-11" />
        )}
        <div className="min-w-0 flex-1">{children}</div>
        {right ?? <span className="size-11" />}
      </div>
    </header>
  )
}

/**
 * Sticky translucent action bar at the bottom; content scrolls under it. It publishes its real height
 * (safe area included) as --bar-h, so <BottomSpacer /> always leaves exactly enough room above it.
 */
export function BottomBar({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const root = document.documentElement
    const publish = () => root.style.setProperty('--bar-h', `${Math.ceil(el.getBoundingClientRect().height)}px`)
    publish()
    const ro = new ResizeObserver(publish)
    ro.observe(el)
    return () => {
      ro.disconnect()
      root.style.removeProperty('--bar-h')
    }
  }, [])
  return (
    <div ref={ref} className="material fixed inset-x-0 bottom-0 z-30 border-t border-line pb-[env(safe-area-inset-bottom)]">
      <div className="mx-auto w-full max-w-md px-5 py-3 md:max-w-[704px] md:px-8">{children}</div>
    </div>
  )
}

/**
 * Room at the end of a page so the last buttons clear the bottom bar: its height (safe area included) plus `gap` px.
 * R8: until the bar has measured itself the fallback assumes a two-button bar, so nothing ever ends up under it.
 */
export function BottomSpacer({ gap = 24 }: { gap?: number }) {
  return <div aria-hidden="true" className="shrink-0" style={{ height: `calc(var(--bar-h, calc(132px + env(safe-area-inset-bottom))) + ${gap}px)` }} />
}
